import { isEqual } from 'lodash';
import { DateTime } from 'luxon';

import { ValueTypeToValue } from 'dashboard/components/FormControlPilledInput/types';
import { GraphExplorerMockDataKey } from 'dashboard/lib/utils/graph';
import {
  DatumType,
  DatumTypeToken,
  DatumValueType,
  GraphId,
  HashableNodeId,
  HashableNodeIdPair,
  hashableNodeIdPairToHashable,
  NodeDataTypeIcon,
  NodeId,
  nodeIdFromHashable,
  nodeIdPairToHashable,
  nodeIdToHashable,
  PersonaPropertyValues,
  PropertySource,
  Tag,
} from 'dashboard/models/graph';
import {
  Backend,
  ServerColorRule,
  ServerEdge,
  ServerExpandResponse,
  ServerHydrateResponse,
  ServerLightlyHydrateResponse,
  ServerNode,
  ServerNodeDatumType,
  ServerNodeExpandability,
  ServerNodeRenderConfig,
  ServerPropertyConfig,
  ServerQueryCursor,
} from 'dashboard/models/graph-backend';
import {
  AccountToken,
  BrowserFingerprint,
  DeviceFingerprintBrowser,
  DeviceFingerprintMobileSdk,
  EmailAddress,
  IpAddress,
  Name,
  PhoneNumber,
  PostalCode,
  ReferenceId,
  SelfieFile,
  ServerHydratedAccount,
  ServerHydratedAccountSimilaritySelfie,
  ServerHydratedAccountStatus,
  ServerHydratedAccountType,
  ServerHydratedAddress,
  ServerHydratedBirthdate,
  ServerHydratedBrowserFingerprint,
  ServerHydratedCustomDatumType,
  ServerHydratedDeviceFingerprint,
  ServerHydratedDeviceToken,
  ServerHydratedDeviceVendorId,
  ServerHydratedEmail,
  ServerHydratedEmailSim,
  ServerHydratedExternalAccount,
  ServerHydratedGovernmentId,
  ServerHydratedIdDocumentNumber,
  ServerHydratedIdNumber,
  ServerHydratedImageBackgroundSim,
  ServerHydratedName,
  ServerHydratedNetwork,
  ServerHydratedNode,
  ServerHydratedPhoneNumber,
  ServerHydratedPostalCode,
  ServerHydratedSsn,
  ServerHydratedSsnLast4,
  ServerHydratedTransactions,
  ServerLightlyHydratedAccount,
  ServerLightlyHydratedExternalAccount,
  ServerLightlyHydratedNode,
  ServerLightlyHydratedTransactions,
  Ssn,
  SsnLast4,
  TransactionAccountInfo,
  TransactionData,
} from 'dashboard/models/graph-hydration';
import {
  AccountsQuery,
  ClustersQuery,
  Conditional,
  Creator,
  GraphQuery,
  GraphQueryParams,
  GraphQueryStatus,
  NHopsFromAccountQuery,
  Operator,
  ParameterMap,
  QueryConditionalLeft,
  QueryType,
  TextValue,
  ValueType,
} from 'dashboard/models/graph-query';
import {
  parseDateTimeRangeConditional,
  parseDateTimeValuesFromConditional,
  parseTextValuesFromConditional,
} from 'dashboard/models/graph-query-conditionals';
import {
  ACCOUNT_DATUM_TYPE,
  ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE,
  ACCOUNT_STATUS_DATUM_TYPE,
  ACCOUNT_TYPE_DATUM_TYPE,
  ACTIVE_ACCOUNT_STATUS,
  ADDRESS_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE,
  DEVICE_TOKEN_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  EXTERNAL_ACCOUNT_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  ID_DOCUMENT_NUMBER_DATUM_TYPE,
  ID_NUMBER_DATUM_TYPE,
  NAME_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE,
  TRANSACTIONS_DATUM_TYPE,
} from 'lib/constants/graph';
import { EmailSimCriteria, ImageBackgroundSimCriteria } from 'lib/constants/graph-datum-type';
import { ApiModel } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';
import { assertNever } from 'lib/utils/types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.DEMO_BACKEND_EXPANSION_DELAY_BASE = 1000;

interface DemoBackendLocalStorage {
  customDatumTypes: Array<ServerNodeDatumType>;
  nodeRenderConfigs: Array<ServerNodeRenderConfig>;
  customDatumTypeCounter: number;
  coloringRules: Array<ServerColorRule>;

  nodes: Array<[HashableNodeId, ServerNode]>;
  edges: Array<[HashableNodeIdPair, ServerEdge]>;
  adjacencyLists: Array<[HashableNodeId, HashableNodeId[]]>;
  nodeHydration: Array<[HashableNodeId, ServerHydratedNode]>;

  liveQueryNodeSets: Array<HashableNodeId[]>;
  liveQueryEdgeSets: Array<Array<[HashableNodeId, HashableNodeId]>>;

  nodeIdCounter: number;
}

enum DemoBackendNodeType {
  Account = 'account',
  AccountStatus = 'account_status',
  AccountType = 'account_type',
  AccountSimilaritySelfie = 'selfie_similarity',
  Address = 'address',
  Birthdate = 'birthdate',
  BrowserFingerprint = 'browser_fingerprint',
  DeviceFingerprintBrowser = 'device_fingerprint',
  DeviceFingerprintMobileSdk = 'device_vendor_id',
  DeviceToken = 'device_token',
  Email = 'email',
  GovernmentId = 'government_id',
  IdDocumentNumber = 'id_document_number',
  IdNumber = 'id_number',
  Name = 'name',
  Network = 'network',
  PhoneNumber = 'phone_number',
  PostalCode = 'postal_code',
  Ssn = 'ssn',
  SsnLast4 = 'ssn_last4',

  // Transaction demo types.
  ExternalAccount = 'external_account',
  Transactions = 'transactions',
}

export enum DemoBackendValueType {
  // Basic value types.
  String = 'string',
  Integer = 'integer',
  Decimal = 'decimal',
  Boolean = 'boolean',

  // More precise types.
  Account = 'account',
  AccountSimilaritySelfie = 'account_similarity_selfie',
  Address = 'address',
  Date = 'date',
  Email = 'email',
  EmailSimilarity = 'email_similarity',
  GovernmentId = 'government_id',
  ImageBackgroundSimilarity = 'image_background_similarity',
  IpAddress = 'ip_address',
  Name = 'name',
  PhoneNumber = 'phone_number',
  PostalCode = 'postal_code',
  SocialSecurityNumber = 'ssn',
  SocialSecurityNumberLast4 = 'ssn_last_4',
  Transactions = 'transactions',
}

type DemoNodes = HashableNodeId[];
type DemoEdges = [HashableNodeId, HashableNodeId][];

class InvalidDemoQueryError extends Error {
  constructor(message: string) {
    super(message);

    Object.setPrototypeOf(this, InvalidDemoQueryError.prototype);
  }
}

export class DemoBackend implements Backend {
  public liveModeEnabled: boolean;
  private customDatumTypesByToken: Map<DatumTypeToken, ServerNodeDatumType>;
  private nodeRenderConfigs: ServerNodeRenderConfig[];
  private propertyConfigs: ServerPropertyConfig[];
  private coloringRules: ServerColorRule[];
  private customDatumTypeCounter: number;

  private nodes: Map<HashableNodeId, ServerNode>;
  private edges: Map<HashableNodeIdPair, ServerEdge>;
  private adjacencyLists: Map<HashableNodeId, Set<HashableNodeId>>;
  private nodeHydration: Map<HashableNodeId, ServerHydratedNode>;

  private liveQueryNodeSets: Array<HashableNodeId[]>;
  private liveQueryEdgeSets: Array<[HashableNodeId, HashableNodeId][]>;
  private liveQueryIndex = 0;

  private get liveQueryNodes(): HashableNodeId[] {
    const nodes = this.liveQueryNodeSets[this.liveQueryIndex];
    return nodes;
  }

  private get liveQueryEdges(): [HashableNodeId, HashableNodeId][] {
    const edges = this.liveQueryEdgeSets[this.liveQueryIndex];
    return edges;
  }

  private nodeIdCounter: number;

  constructor(liveModeEnabled: boolean) {
    this.liveModeEnabled = liveModeEnabled;
    this.customDatumTypesByToken = new Map();
    this.customDatumTypeCounter = 0;
    this.propertyConfigs = [];
    this.nodeRenderConfigs = [];
    this.coloringRules = [];

    this.nodes = new Map();
    this.edges = new Map();
    this.adjacencyLists = new Map();
    this.nodeHydration = new Map();

    this.liveQueryNodeSets = [];
    this.liveQueryEdgeSets = [];

    this.nodeIdCounter = 0;
  }

  // ----------------------------------------------------------------------------------------------------
  // Helper functions
  // ----------------------------------------------------------------------------------------------------

  private makeHashableNodeIdPair(hashableNodeId1: HashableNodeId, hashableNodeId2: HashableNodeId) {
    const nodeId1 = nodeIdFromHashable(hashableNodeId1);
    const nodeId2 = nodeIdFromHashable(hashableNodeId2);
    return nodeIdPairToHashable(nodeId1, nodeId2);
  }

  private makeServerNode(hashableNodeId: HashableNodeId): ServerNode {
    const nodeId = nodeIdFromHashable(hashableNodeId);
    return {
      'datum-type': nodeId.token,
      value: nodeId.value,
    };
  }

  private makeServerEdge(
    hashableNodeId1: HashableNodeId,
    hashableNodeId2: HashableNodeId,
  ): ServerEdge {
    return {
      node1: this.makeServerNode(hashableNodeId1),
      node2: this.makeServerNode(hashableNodeId2),
    };
  }

  private makeServerNodes(nodes: HashableNodeId[]): ServerNode[] {
    return nodes.map(this.makeServerNode);
  }

  private getNonfetchedNodeCount(
    hashableNodeId: HashableNodeId,
    visibleSet: Set<HashableNodeId>,
  ): number {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return Array.from(this.adjacencyLists.get(hashableNodeId).values()).filter(
      (neighborHashableNodeId) => !visibleSet.has(neighborHashableNodeId),
    ).length;
  }

  // Returns null if the node is fully expanded already.
  private makeServerNodeExpandability(
    hashableNodeId: HashableNodeId,
    visibleSet: Set<HashableNodeId>,
  ): ServerNodeExpandability | null {
    const nonfetchedNodeCount = this.getNonfetchedNodeCount(hashableNodeId, visibleSet);
    if (nonfetchedNodeCount === 0) {
      return null;
    }
    return {
      node: this.makeServerNode(hashableNodeId),
      'nonfetched-edge-count': nonfetchedNodeCount,
    };
  }

  // ----------------------------------------------------------------------------------------------------
  // In-memory graph query execution
  // ----------------------------------------------------------------------------------------------------

  private hasDatumTypeToken(
    hashableNodeId: HashableNodeId,
    datumTypeToken: DatumTypeToken,
  ): boolean {
    return nodeIdFromHashable(hashableNodeId).token === datumTypeToken;
  }

  private isAccountHashableNodeId(hashableNodeId: HashableNodeId): boolean {
    return (
      this.hasDatumTypeToken(hashableNodeId, ACCOUNT_DATUM_TYPE.token) ||
      this.hasDatumTypeToken(hashableNodeId, EXTERNAL_ACCOUNT_DATUM_TYPE.token)
    );
  }

  private getAccountNeighbors(
    accountHashableNodeId: HashableNodeId,
    datumTypeToken: DatumTypeToken,
  ): HashableNodeId[] {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return Array.from(this.adjacencyLists.get(accountHashableNodeId)).filter(
      (neighbor) => nodeIdFromHashable(neighbor).token === datumTypeToken,
    );
  }

  private getTransactionsHashableNodeId(transactionId: string): HashableNodeId[] {
    const results: HashableNodeId[] = [];

    this.nodeHydration.forEach((hydratedNode, hashableNodeId) => {
      const nodeId = nodeIdFromHashable(hashableNodeId);
      if (nodeId.token !== TRANSACTIONS_DATUM_TYPE.token) {
        return;
      }
      const hydratedTransactionsNode = hydratedNode as ServerHydratedTransactions;
      const transactionMatches = hydratedTransactionsNode.transactions.filter(
        (transactionData) => transactionData.id === transactionId,
      );
      if (transactionMatches.length > 0) {
        results.push(hashableNodeId);
      }
    });
    return results;
  }

  private accountSatisfiesConditional(
    accountHashableNodeId: HashableNodeId,
    conditional: Conditional,
  ): boolean {
    const hydratedAccount: ServerHydratedAccount = this.nodeHydration.get(
      accountHashableNodeId,
    ) as ServerHydratedAccount;

    switch (conditional.left as QueryConditionalLeft) {
      case ACCOUNT_DATUM_TYPE.name: {
        // Account ID equals [right]
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected account_id equals [right]');
        }

        const accountIds = parseTextValuesFromConditional(conditional);
        return accountIds.includes(hydratedAccount.token);
      }
      case 'reference_id': {
        // Account Reference ID equals [right]
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected ref_id equals [right]');
        }

        const refIds = parseTextValuesFromConditional(conditional);
        return refIds.includes(hydratedAccount['reference-id']);
      }
      case BIRTHDATE_DATUM_TYPE.name: {
        let condition: (accountBirthdates: DateTime[]) => boolean;

        if (conditional.operator === Operator.Equals) {
          // Birthdate equals [right]
          const birthdates = parseDateTimeValuesFromConditional(conditional);

          condition = (accountBirthdates: DateTime[]) => {
            return accountBirthdates.some((accountBirthdate) => {
              return birthdates.some(
                (birthdate) =>
                  birthdate.startOf('day').toISO() === accountBirthdate.startOf('day').toISO(),
              );
            });
          };
        } else if (conditional.operator === Operator.IsNotNull) {
          const rightValues = parseTextValuesFromConditional(conditional);
          if (rightValues.length !== 0) {
            throw new InvalidDemoQueryError('Expected Birthdate Is Not Null with no arguments');
          }
          condition = (accountBirthdates: DateTime[]) => {
            return accountBirthdates.length > 0;
          };
        } else if (conditional.operator === Operator.IsNull) {
          const rightValues = parseTextValuesFromConditional(conditional);
          if (rightValues.length !== 0) {
            throw new InvalidDemoQueryError('Expected Birthdate Is Null with no arguments');
          }
          condition = (accountBirthdates: DateTime[]) => {
            return accountBirthdates.length === 0;
          };
        } else {
          // Birthdate IsBefore/IsAfter/IsBetween [right]
          const [lowerBound, upperBound] = parseDateTimeRangeConditional(conditional);

          condition = (accountBirthdates: DateTime[]) => {
            return accountBirthdates.some((accountBirthdate) => {
              if (lowerBound != null && accountBirthdate < lowerBound) {
                return false;
              }
              if (upperBound != null && accountBirthdate > upperBound) {
                return false;
              }
              return true;
            });
          };
        }

        const birthdates = this.getAccountNeighbors(
          accountHashableNodeId,
          BIRTHDATE_DATUM_TYPE.token,
        ).map((neighbor) => {
          const hydratedBirthdate = this.nodeHydration.get(neighbor) as ServerHydratedBirthdate;
          return DateTime.fromISO(hydratedBirthdate.birthdate);
        });

        return condition(birthdates);
      }
      case NAME_DATUM_TYPE.name: {
        const names = parseTextValuesFromConditional(conditional).map((name) =>
          name.toLocaleLowerCase(),
        );

        let condition: (account: ServerHydratedAccount) => boolean;

        if (conditional.operator === Operator.Matches) {
          condition = (account: ServerHydratedAccount) => {
            const firstName = account.name.first?.toLocaleLowerCase();
            const middleName = account.name.middle?.toLocaleLowerCase();
            const lastName = account.name.last?.toLocaleLowerCase();
            return (
              names.includes(`${firstName} ${lastName}`) ||
              names.includes(`${lastName} ${firstName}`) ||
              names.includes(`${firstName} ${middleName} ${lastName}`) ||
              names.includes(`${lastName} ${firstName} ${middleName}`)
            );
          };
        } else if (conditional.operator === Operator.PartiallyMatches) {
          condition = (account: ServerHydratedAccount) => {
            const firstName = account.name.first?.toLocaleLowerCase();
            const middleName = account.name.middle?.toLocaleLowerCase();
            const lastName = account.name.last?.toLocaleLowerCase();
            return (
              // Include full matches too.
              names.includes(`${firstName} ${lastName}`) ||
              names.includes(`${lastName} ${firstName}`) ||
              names.includes(`${firstName} ${middleName} ${lastName}`) ||
              names.includes(`${lastName} ${firstName} ${middleName}`) ||
              // Check for substring matches.
              names.some((name) => firstName?.toLocaleLowerCase()?.includes(name)) ||
              names.some((name) => middleName?.toLocaleLowerCase()?.includes(name)) ||
              names.some((name) => lastName?.toLocaleLowerCase()?.includes(name))
            );
          };
        } else if (conditional.operator === Operator.IsNotNull) {
          const rightValues = parseTextValuesFromConditional(conditional);
          if (rightValues.length !== 0) {
            throw new InvalidDemoQueryError('Expected Name Is Not Null with no arguments');
          }
          condition = (account: ServerHydratedAccount) => {
            return !!account.name.first || !!account.name.middle || !!account.name.last;
          };
        } else if (conditional.operator === Operator.IsNull) {
          const rightValues = parseTextValuesFromConditional(conditional);
          if (rightValues.length !== 0) {
            throw new InvalidDemoQueryError('Expected Name Is Null with no arguments');
          }
          condition = (account: ServerHydratedAccount) => {
            return !account.name.first && !account.name.middle && !account.name.last;
          };
        } else {
          throw new InvalidDemoQueryError('Expected name Matches/PartiallyMatches/Is [right]');
        }

        return condition(hydratedAccount);
      }
      case BROWSER_FINGERPRINT_DATUM_TYPE.name: {
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected browser_fingerprint equals [right]');
        }
        const browserFingerprints = parseTextValuesFromConditional(conditional);
        return this.getAccountNeighbors(
          accountHashableNodeId,
          BROWSER_FINGERPRINT_DATUM_TYPE.token,
        ).some((neighbor) => {
          const hydratedBrowserFingerprint = this.nodeHydration.get(
            neighbor,
          ) as ServerHydratedBrowserFingerprint;
          return browserFingerprints.includes(hydratedBrowserFingerprint['browser-fingerprint']);
        });
      }
      case DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.name: {
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected device_fingerprint equals [right]');
        }
        const deviceFingerprints = parseTextValuesFromConditional(conditional);
        return this.getAccountNeighbors(
          accountHashableNodeId,
          DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
        ).some((neighbor) => {
          const hydratedDeviceFingerprint = this.nodeHydration.get(
            neighbor,
          ) as ServerHydratedDeviceFingerprint;
          return deviceFingerprints.includes(hydratedDeviceFingerprint['device-fingerprint']);
        });
      }
      case DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.name: {
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected device_vendor_id equals [right]');
        }
        const deviceVendorIds = parseTextValuesFromConditional(conditional);
        return this.getAccountNeighbors(
          accountHashableNodeId,
          DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token,
        ).some((neighbor) => {
          const hydratedDeviceVendorId = this.nodeHydration.get(
            neighbor,
          ) as ServerHydratedDeviceVendorId;
          return deviceVendorIds.includes(hydratedDeviceVendorId['device-vendor-id']);
        });
      }
      case EMAIL_DATUM_TYPE.name: {
        switch (conditional.operator) {
          case Operator.Equals: {
            const emailAddresses = parseTextValuesFromConditional(conditional);
            return this.getAccountNeighbors(accountHashableNodeId, EMAIL_DATUM_TYPE.token).some(
              (neighbor) => {
                const hydratedEmailAddress = this.nodeHydration.get(
                  neighbor,
                ) as ServerHydratedEmail;
                return emailAddresses.some((emailAddress) => {
                  // Note: Technically, domain names should be case sensitive, but we're ignoring it now for simplicity.
                  const queryValue = emailAddress.toLocaleLowerCase();
                  const accountEmailAddress = hydratedEmailAddress.email.toLocaleLowerCase();

                  // Check for a full match.
                  if (queryValue === accountEmailAddress) {
                    return true;
                  }
                  // Check for a domain match (eg. @mydomain.com)
                  if (queryValue.startsWith('@') && accountEmailAddress.endsWith(queryValue)) {
                    return true;
                  }
                  // Check for a username match (eg. username@)
                  if (queryValue.endsWith('@') && accountEmailAddress.startsWith(queryValue)) {
                    return true;
                  }
                  return false;
                });
              },
            );
          }
          case Operator.IsNotNull: {
            const rightValues = parseTextValuesFromConditional(conditional);
            if (rightValues.length !== 0) {
              throw new InvalidDemoQueryError('Expected Email Is Not Null with no arguments');
            }

            const emailNeighbors = this.getAccountNeighbors(
              accountHashableNodeId,
              EMAIL_DATUM_TYPE.token,
            );
            return emailNeighbors.length > 0;
          }
          case Operator.IsNull: {
            const rightValues = parseTextValuesFromConditional(conditional);
            if (rightValues.length !== 0) {
              throw new InvalidDemoQueryError('Expected Email Is Null with no arguments');
            }

            const emailNeighbors = this.getAccountNeighbors(
              accountHashableNodeId,
              EMAIL_DATUM_TYPE.token,
            );
            return emailNeighbors.length === 0;
          }
          default:
            throw new InvalidDemoQueryError('Expected Email equals [right]');
        }
      }
      case GOVERNMENT_ID_DATUM_TYPE.name: {
        switch (conditional.operator) {
          case Operator.Equals: {
            const governmentIds = parseTextValuesFromConditional(conditional);
            return this.getAccountNeighbors(
              accountHashableNodeId,
              GOVERNMENT_ID_DATUM_TYPE.token,
            ).some((neighbor) => {
              const hydratedGovernmentId = this.nodeHydration.get(
                neighbor,
              ) as ServerHydratedGovernmentId;
              return governmentIds.includes(hydratedGovernmentId['id']);
            });
          }
          case Operator.IsNotNull: {
            const rightValues = parseTextValuesFromConditional(conditional);
            if (rightValues.length !== 0) {
              throw new InvalidDemoQueryError(
                'Expected GovernmentId Is Not Null with no arguments]',
              );
            }

            const governmentIdNeighbors = this.getAccountNeighbors(
              accountHashableNodeId,
              GOVERNMENT_ID_DATUM_TYPE.token,
            );
            return governmentIdNeighbors.length > 0;
          }
          case Operator.IsNull: {
            const rightValues = parseTextValuesFromConditional(conditional);
            if (rightValues.length !== 0) {
              throw new InvalidDemoQueryError('Expected GovernmentId Is Null with no arguments]');
            }

            const governmentIdNeighbors = this.getAccountNeighbors(
              accountHashableNodeId,
              GOVERNMENT_ID_DATUM_TYPE.token,
            );
            return governmentIdNeighbors.length === 0;
          }
          default:
            throw new InvalidDemoQueryError('Expected government_id equals/is [right]');
        }
      }
      case SOCIAL_SECURITY_NUMBER_DATUM_TYPE.name: {
        switch (conditional.operator) {
          case Operator.Equals: {
            const ssns = parseTextValuesFromConditional(conditional);
            return this.getAccountNeighbors(
              accountHashableNodeId,
              SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
            ).some((neighbor) => {
              const hydratedSsn = this.nodeHydration.get(neighbor) as ServerHydratedSsn;
              return ssns.includes(hydratedSsn['ssn']);
            });
          }
          case Operator.IsNotNull: {
            const rightValues = parseTextValuesFromConditional(conditional);
            if (rightValues.length !== 0) {
              throw new InvalidDemoQueryError('Expected SSN Is Not Null with no arguments');
            }

            const ssnNeighbors = this.getAccountNeighbors(
              accountHashableNodeId,
              SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
            );
            return ssnNeighbors.length > 0;
          }
          case Operator.IsNull: {
            const rightValues = parseTextValuesFromConditional(conditional);
            if (rightValues.length !== 0) {
              throw new InvalidDemoQueryError('Expected SSN Is Null with no arguments');
            }

            const ssnNeighbors = this.getAccountNeighbors(
              accountHashableNodeId,
              SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
            );
            return ssnNeighbors.length === 0;
          }
          default:
            throw new InvalidDemoQueryError('Expected SSN equals [right]');
        }
      }
      case PHONE_NUMBER_DATUM_TYPE.name: {
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected phone_number equals [right]');
        }
        const phoneNumbers = parseTextValuesFromConditional(conditional);
        return this.getAccountNeighbors(accountHashableNodeId, PHONE_NUMBER_DATUM_TYPE.token).some(
          (neighbor) => {
            const hydratedPhoneNumber = this.nodeHydration.get(
              neighbor,
            ) as ServerHydratedPhoneNumber;
            const accountPhoneNumber = hydratedPhoneNumber['phone'].replace(/\D/g, '');

            return phoneNumbers.some((phoneNumber) => {
              return phoneNumber.replace(/\D/g, '') === accountPhoneNumber;
            });
          },
        );
      }
      case NETWORK_DATUM_TYPE.name:
      case 'ip_address': {
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected ip_address equals [right]');
        }
        const ipAddresses = parseTextValuesFromConditional(conditional);
        return this.getAccountNeighbors(accountHashableNodeId, NETWORK_DATUM_TYPE.token).some(
          (neighbor) => {
            const hydratedIpAddress = this.nodeHydration.get(neighbor) as ServerHydratedNetwork;
            return ipAddresses.includes(hydratedIpAddress['ip']);
          },
        );
      }
      case 'created_at': {
        const [lowerBound, upperBound] = parseDateTimeRangeConditional(conditional);

        const createdAt = DateTime.fromISO(hydratedAccount['created-at']);
        if (lowerBound != null && createdAt < lowerBound) {
          return false;
        }
        if (upperBound != null && createdAt > upperBound) {
          return false;
        }
        return true;
      }
      case ACCOUNT_STATUS_DATUM_TYPE.name: {
        // Note: We do account statuses differently for demo backend, because we don't actually fetch account status
        //       objects and make the entire React component rerender with the new values.
        // TODO(kerry): Use tokens and do it like the real backend.
        return conditional.right
          .map((value) => (value as ValueTypeToValue[ValueType])['raw-value'].toUpperCase())
          .includes(hydratedAccount.status.toUpperCase());
      }
      case 'transaction_id': {
        // Transaction ID equals [right]
        if (conditional.operator !== Operator.Equals) {
          throw new InvalidDemoQueryError('Expected transaction ID equals [right]');
        }

        const transactionIds = parseTextValuesFromConditional(conditional);
        // Look up the transactions object that contains the transaction ID.
        const transactionHashableNodeIds = transactionIds
          .map((transactionId) => this.getTransactionsHashableNodeId(transactionId))
          .flat();
        const accountTransactionInfos = transactionHashableNodeIds
          .map((hashableNodeId) => {
            const hydratedTransactions = this.nodeHydration.get(
              hashableNodeId,
            ) as ServerHydratedTransactions;
            return [hydratedTransactions.account1, hydratedTransactions.account2];
          })
          .flat();
        return accountTransactionInfos.some((transactionInfo) =>
          isEqual(hydratedAccount['transaction-account-info'], transactionInfo),
        );
      }
    }

    // Default to true for any other conditionals.
    return true;
  }

  private filterAccounts(accounts: HashableNodeId[], conditional: Conditional): HashableNodeId[] {
    return accounts.filter((accountHashableNodeId) =>
      this.accountSatisfiesConditional(accountHashableNodeId, conditional),
    );
  }

  private fetchAllEdges(queryNodes: DemoNodes, knownNodes: HashableNodeId[]): DemoEdges {
    const queryEdges: DemoEdges = [];
    const queryNodesSet: Set<HashableNodeId> = new Set(queryNodes);
    const knownNodesSet: Set<HashableNodeId> = new Set(knownNodes);
    const includedQueryEdges: Set<HashableNodeIdPair> = new Set();

    queryNodes.forEach((queryNode) => {
      const neighbors = this.adjacencyLists.get(queryNode);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      neighbors.forEach((neighbor) => {
        if (!queryNodesSet.has(neighbor) && !knownNodesSet.has(neighbor)) {
          return;
        }

        // We have an edge between the node and its neighbor.
        const hashableNodeIdPair = hashableNodeIdPairToHashable(queryNode, neighbor);
        if (includedQueryEdges.has(hashableNodeIdPair)) {
          // We already have an edge.
          return;
        }

        // Add in the edge.
        queryEdges.push([queryNode, neighbor]);
        includedQueryEdges.add(hashableNodeIdPair);
      });
    });

    return queryEdges;
  }

  private fetchAllAccounts(): HashableNodeId[] {
    const allAccounts = Array.from(this.nodes.keys()).filter((hashableNodeId) =>
      this.hasDatumTypeToken(hashableNodeId, ACCOUNT_DATUM_TYPE.token),
    );

    // Sort the accounts by (created_at [descending], token [ascending]).
    allAccounts.sort((hashableNodeId1, hashableNodeId2) => {
      const hydratedAccount1 = this.nodeHydration.get(hashableNodeId1) as ServerHydratedAccount;
      const hydratedAccount2 = this.nodeHydration.get(hashableNodeId2) as ServerHydratedAccount;

      const createdAt1 = DateTime.fromISO(hydratedAccount1['created-at']);
      const createdAt2 = DateTime.fromISO(hydratedAccount1['created-at']);
      if (createdAt1 < createdAt2) {
        return 1;
      } else if (createdAt1 > createdAt2) {
        return -1;
      }
      return hydratedAccount1.token.localeCompare(hydratedAccount2.token);
    });

    return allAccounts;
  }

  private applyCursorFilter(
    accounts: HashableNodeId[],
    cursor: ServerQueryCursor,
  ): HashableNodeId[] {
    if (cursor == null || cursor === '') {
      return accounts;
    }

    const [cursorCreatedAtTimestamp, cursorAccountToken] = JSON.parse(cursor);
    const cursorCreatedAt = DateTime.fromISO(cursorCreatedAtTimestamp);

    return accounts.filter((hashableNodeId) => {
      const hydratedAccount = this.nodeHydration.get(hashableNodeId) as ServerHydratedAccount;
      const createdAt = DateTime.fromISO(hydratedAccount['created-at']);
      const accountToken = hydratedAccount.token;

      if (createdAt < cursorCreatedAt) {
        return true;
      } else if (createdAt > cursorCreatedAt) {
        return false;
      }

      return accountToken.localeCompare(cursorAccountToken) >= 0;
    });
  }

  private accountToCursor(accountHashableNodeId: HashableNodeId): ServerQueryCursor {
    const hydratedAccount = this.nodeHydration.get(accountHashableNodeId) as ServerHydratedAccount;
    const createdAt = DateTime.fromISO(hydratedAccount['created-at']).toISO();
    const accountToken = hydratedAccount.token;

    return JSON.stringify([createdAt, accountToken]);
  }

  private performAccountsQuery(
    queryParams: AccountsQuery,
  ): [DemoNodes, DemoEdges, ServerQueryCursor, DemoNodes] {
    const allAccounts = this.fetchAllAccounts();

    let matchingAccounts = this.applyCursorFilter(allAccounts, queryParams['start-cursor']);

    queryParams.conditionals.forEach((conditional) => {
      matchingAccounts = this.filterAccounts(matchingAccounts, conditional);
    });

    const queryNodes: HashableNodeId[] = [];
    const knownNodes = new Set(queryParams['known-node-ids'].map(nodeIdToHashable));
    let nextCursor: ServerQueryCursor = '';
    matchingAccounts.forEach((account) => {
      if (queryNodes.length >= queryParams['node-limit']) {
        return;
      }
      if (knownNodes.has(account)) {
        return;
      }
      queryNodes.push(account);
      nextCursor = this.accountToCursor(account);
    });

    // Now do a one hop on the nodes.
    const seenQueryNodes: Set<HashableNodeId> = new Set(matchingAccounts);
    matchingAccounts.forEach((hashableNodeId) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.adjacencyLists.get(hashableNodeId).forEach((neighbor) => {
        if (queryNodes.length >= queryParams['node-limit']) {
          return;
        }
        if (knownNodes.has(hashableNodeId)) {
          return;
        }
        if (seenQueryNodes.has(neighbor)) {
          return;
        }
        queryNodes.push(neighbor);
        seenQueryNodes.add(neighbor);
      });
    });

    const queryEdges = this.fetchAllEdges(queryNodes, Array.from(knownNodes));
    const seedNodes = queryNodes.filter((hashableNodeId) =>
      matchingAccounts.includes(hashableNodeId),
    );
    return [queryNodes, queryEdges, nextCursor, seedNodes];
  }

  // Returns [shared_properties_criteria, created_at_conditional], where
  // shared_properties_criteria is an OR or ANDs of datum type tokens.
  private validateClustersQuery(queryParams: ClustersQuery): [DatumTypeToken[][], Conditional] {
    // Parse the conditionals.
    let sharedPropertiesConditional: Conditional | null = null;
    let createdAtConditional: Conditional | null = null;

    queryParams.conditionals.forEach((conditional) => {
      switch (conditional.left as QueryConditionalLeft) {
        case 'shared_properties': {
          if (sharedPropertiesConditional != null) {
            throw new InvalidDemoQueryError('More than one shared properties conditional?');
          }
          sharedPropertiesConditional = conditional;
          break;
        }
        case 'created_at': {
          if (createdAtConditional != null) {
            throw new InvalidDemoQueryError('More than one created_at conditional?');
          }
          createdAtConditional = conditional;
          break;
        }
        default: {
          throw new InvalidDemoQueryError('Invalid conditional for clusters query');
        }
      }
    });

    if (sharedPropertiesConditional == null) {
      throw new InvalidDemoQueryError('Missing shared properties conditional?');
    }
    if (createdAtConditional == null) {
      throw new InvalidDemoQueryError('Missing created_at conditional?');
    }

    // Parse the shared properties.
    let sharedPropertiesCriteria: DatumTypeToken[][];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    switch (sharedPropertiesConditional.operator) {
      case Operator.IncludesAnyOf: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sharedPropertiesCriteria = sharedPropertiesConditional.right.map((value) => [
          (value as TextValue).value,
        ]);
        break;
      }
      case Operator.IncludesAllOf: {
        sharedPropertiesCriteria = [
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          sharedPropertiesConditional.right.map((value) => (value as TextValue).value),
        ];
        break;
      }
      default: {
        throw new InvalidDemoQueryError('Invalid shared properties conditional for clusters query');
      }
    }

    return [sharedPropertiesCriteria, createdAtConditional];
  }

  private performClustersQuery(
    queryParams: ClustersQuery,
  ): [DemoNodes, DemoEdges, ServerQueryCursor, DemoNodes] {
    const allAccounts = this.fetchAllAccounts();

    const [sharedPropertiesCriteria, createdAtConditional] =
      this.validateClustersQuery(queryParams);

    // Do some initial filtering.
    const matchingAccounts = this.applyCursorFilter(
      this.filterAccounts(allAccounts, createdAtConditional),
      queryParams['start-cursor'],
    );

    const matchingAccountsSet: Set<HashableNodeId> = new Set(matchingAccounts);

    // Do BFS on the accounts.
    let nextCursor: ServerQueryCursor = '';

    const getNeighbors = (hashableNodeId: HashableNodeId) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Array.from(this.adjacencyLists.get(hashableNodeId));
    const hasDatumTypeToken = (hashableNodeId: HashableNodeId, datumTypeToken: DatumTypeToken) =>
      nodeIdFromHashable(hashableNodeId).token === datumTypeToken;

    const queryNodes: HashableNodeId[] = [];
    const knownNodes = new Set(queryParams['known-node-ids'].map(nodeIdToHashable));
    const seenInResult: Set<HashableNodeId> = new Set();

    matchingAccounts.forEach((startingHashableNodeId) => {
      // Start the BFS here.
      if (queryNodes.length < queryParams['node-limit']) {
        nextCursor = this.accountToCursor(startingHashableNodeId);
      } else {
        return;
      }

      // The queue entries are: [hashableNodeId, hopDistance].
      const queue: [HashableNodeId, number][] = [[startingHashableNodeId, 0]];
      const isInCluster: Set<HashableNodeId> = new Set([startingHashableNodeId]);
      const currentCluster: [HashableNodeId, number][] = [[startingHashableNodeId, 0]];
      let nextIndexToProcess = 0;

      while (nextIndexToProcess < queue.length) {
        const [currentHashableNodeId, distance] = queue[nextIndexToProcess];

        sharedPropertiesCriteria.forEach((allOfCriterion) => {
          // Find all matching accounts that are two hops away, that satisfy the all-of datum types.

          const isValidObjectNode = (objectHashableNodeId: HashableNodeId) =>
            allOfCriterion.includes(nodeIdFromHashable(objectHashableNodeId).token);

          let twoHopAccountNodes: HashableNodeId[];

          allOfCriterion.forEach((datumTypeToken, index) => {
            // Find one hop object nodes.
            const oneHopObjectNodes = getNeighbors(currentHashableNodeId).filter(
              (objectHashableNodeId) => hasDatumTypeToken(objectHashableNodeId, datumTypeToken),
            );

            // Find two hop account nodes.
            const twoHopAccountsViaDatumTypeObject: Set<HashableNodeId> = new Set();
            oneHopObjectNodes.forEach((objectHashableNodeId) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              this.adjacencyLists.get(objectHashableNodeId).forEach((accountHashableNodeId) => {
                if (!this.isAccountHashableNodeId(accountHashableNodeId)) {
                  return;
                }
                if (!matchingAccountsSet.has(accountHashableNodeId)) {
                  return;
                }
                if (currentHashableNodeId === accountHashableNodeId) {
                  // Don't go back to the same account node.
                  return;
                }
                twoHopAccountsViaDatumTypeObject.add(accountHashableNodeId);
              });
            });

            if (index === 0) {
              // Grab everything if it's the first datum type token.
              twoHopAccountNodes = Array.from(twoHopAccountsViaDatumTypeObject);
            } else {
              // Filter down the existing list, if it's not the first datum type token, since we're doing
              // an all-of.
              twoHopAccountNodes = twoHopAccountNodes.filter((hashableNodeId) =>
                twoHopAccountsViaDatumTypeObject.has(hashableNodeId),
              );
            }
          });

          // We have our filtered list of nodes two hops away.

          // Find the object nodes that were involved in the two hops.
          const forwardObjectNodes = Array.from(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.adjacencyLists.get(currentHashableNodeId),
          ).filter(isValidObjectNode);
          const backwardObjectNodes = new Set(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            twoHopAccountNodes
              .map((accountHashableNodeId) =>
                getNeighbors(accountHashableNodeId).filter(isValidObjectNode),
              )
              .flat(),
          );
          // Find the intersection.
          const oneHopObjectNodes = forwardObjectNodes.filter((objectNode) =>
            backwardObjectNodes.has(objectNode),
          );

          // Process each of the object nodes that are one hop away.
          oneHopObjectNodes.forEach((hashableNodeId) => {
            if (isInCluster.has(hashableNodeId)) {
              return;
            }
            isInCluster.add(hashableNodeId);
            currentCluster.push([hashableNodeId, distance + 1]);
          });

          // Process each of the account nodes that are two hops away.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          twoHopAccountNodes.forEach((hashableNodeId) => {
            if (isInCluster.has(hashableNodeId)) {
              return;
            }
            queue.push([hashableNodeId, distance + 2]);
            isInCluster.add(hashableNodeId);
            currentCluster.push([hashableNodeId, distance + 2]);
          });
        });

        nextIndexToProcess += 1;
      }

      // Filter by number of hops.
      const hopCountFilteredNodes = currentCluster
        .filter(([_hashableNodeId, distance]) => distance <= queryParams['num-hops'])
        .map(([hashableNodeId, _distance]) => hashableNodeId);

      // Check the cluster size.
      if (hopCountFilteredNodes.length < queryParams['min-cluster-size']) {
        return;
      }

      hopCountFilteredNodes.forEach((hashableNodeId) => {
        if (queryNodes.length >= queryParams['node-limit']) {
          return;
        }
        if (knownNodes.has(hashableNodeId)) {
          return;
        }
        if (seenInResult.has(hashableNodeId)) {
          return;
        }
        queryNodes.push(hashableNodeId);
        seenInResult.add(hashableNodeId);
      });
    });

    const queryEdges = this.fetchAllEdges(queryNodes, Array.from(knownNodes));
    const seedNodes = queryNodes.filter((hashableNodeId) =>
      matchingAccounts.includes(hashableNodeId),
    );
    return [queryNodes, queryEdges, nextCursor, seedNodes];
  }

  private validateNHopSharedPropertiesConditional(
    queryParams: NHopsFromAccountQuery,
  ): DatumTypeToken[][] {
    // Parse the conditionals.
    let sharedPropertiesConditional: Conditional | null = null;

    queryParams.conditionals.forEach((conditional) => {
      switch (conditional.left as QueryConditionalLeft) {
        case 'shared_properties': {
          if (sharedPropertiesConditional != null) {
            throw new InvalidDemoQueryError('More than one shared properties conditional?');
          }
          sharedPropertiesConditional = conditional;
          break;
        }
        default: {
          break;
        }
      }
    });

    if (sharedPropertiesConditional == null) {
      throw new InvalidDemoQueryError('Missing shared properties conditional?');
    }

    // Parse the shared properties.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    switch (sharedPropertiesConditional.operator) {
      case Operator.IncludesAnyOf: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return sharedPropertiesConditional.right.map((value) => [(value as TextValue).value]);
      }
      case Operator.IncludesAllOf: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return [sharedPropertiesConditional.right.map((value) => (value as TextValue).value)];
      }
      default: {
        throw new InvalidDemoQueryError('Invalid shared properties conditional for clusters query');
      }
    }
  }

  private performNHopsQuery(
    queryParams: NHopsFromAccountQuery,
  ): [DemoNodes, DemoEdges, ServerQueryCursor, DemoNodes] {
    const sharedPropertiesCriteria = this.validateNHopSharedPropertiesConditional(queryParams);

    // Do the initial filtering.
    let matchingAccounts = this.applyCursorFilter(
      this.fetchAllAccounts(),
      queryParams['start-cursor'],
    );

    queryParams.conditionals.forEach((conditional) => {
      matchingAccounts = this.filterAccounts(matchingAccounts, conditional);
    });

    // Do BFS on the account(s).
    let nextCursor: ServerQueryCursor = '';

    const getNeighbors = (hashableNodeId: HashableNodeId) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Array.from(this.adjacencyLists.get(hashableNodeId));
    const hasDatumTypeToken = (hashableNodeId: HashableNodeId, datumTypeToken: DatumTypeToken) =>
      nodeIdFromHashable(hashableNodeId).token === datumTypeToken;

    const queryNodes: HashableNodeId[] = [];
    const knownNodes = new Set(queryParams['known-node-ids'].map(nodeIdToHashable));
    const seenInResult: Set<HashableNodeId> = new Set();

    matchingAccounts.forEach((startingHashableNodeId) => {
      // Start the BFS here.
      if (queryNodes.length < queryParams['node-limit']) {
        nextCursor = this.accountToCursor(startingHashableNodeId);
      } else {
        return;
      }

      // The queue entries are: [hashableNodeId, hopDistance].
      const queue: [HashableNodeId, number][] = [[startingHashableNodeId, 0]];
      const isInCluster: Set<HashableNodeId> = new Set([startingHashableNodeId]);
      const currentCluster: [HashableNodeId, number][] = [[startingHashableNodeId, 0]];
      let nextIndexToProcess = 0;

      while (nextIndexToProcess < queue.length) {
        const [currentHashableNodeId, distance] = queue[nextIndexToProcess];

        sharedPropertiesCriteria.forEach((allOfCriterion) => {
          // Find all matching accounts that are two hops away, that satisfy the all-of datum types.

          const isValidObjectNode = (objectHashableNodeId: HashableNodeId) =>
            allOfCriterion.includes(nodeIdFromHashable(objectHashableNodeId).token);

          let twoHopAccountNodes: HashableNodeId[];

          allOfCriterion.forEach((datumTypeToken, index) => {
            // Find one hop object nodes.
            const oneHopObjectNodes = getNeighbors(currentHashableNodeId).filter(
              (objectHashableNodeId) => hasDatumTypeToken(objectHashableNodeId, datumTypeToken),
            );

            // Find two hop account nodes.
            const twoHopAccountsViaDatumTypeObject: Set<HashableNodeId> = new Set();
            oneHopObjectNodes.forEach((objectHashableNodeId) => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              this.adjacencyLists.get(objectHashableNodeId).forEach((accountHashableNodeId) => {
                if (!this.isAccountHashableNodeId(accountHashableNodeId)) {
                  return;
                }
                if (currentHashableNodeId === accountHashableNodeId) {
                  // Don't go back to the same account node.
                  return;
                }

                // NOTE: Here, unlike by-cluster queries, we don't check that the connected accounts
                //       fulfill the search criteria.
                twoHopAccountsViaDatumTypeObject.add(accountHashableNodeId);
              });
            });

            if (index === 0) {
              // Grab everything if it's the first datum type token.
              twoHopAccountNodes = Array.from(twoHopAccountsViaDatumTypeObject);
            } else {
              // Filter down the existing list, if it's not the first datum type token, since we're doing
              // an all-of.
              twoHopAccountNodes = twoHopAccountNodes.filter((hashableNodeId) =>
                twoHopAccountsViaDatumTypeObject.has(hashableNodeId),
              );
            }
          });

          // We filter by the traversal criteria.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          queryParams['traversal-conditionals'].forEach((conditional) => {
            twoHopAccountNodes = this.filterAccounts(twoHopAccountNodes, conditional);
          });

          // We have our filtered list of nodes two hops away.

          // Find the object nodes that were involved in the two hops.
          const forwardObjectNodes = Array.from(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            this.adjacencyLists.get(currentHashableNodeId),
          ).filter(isValidObjectNode);
          const backwardObjectNodes = new Set(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            twoHopAccountNodes
              .map((accountHashableNodeId) =>
                getNeighbors(accountHashableNodeId).filter(isValidObjectNode),
              )
              .flat(),
          );
          // Find the intersection.
          const oneHopObjectNodes = forwardObjectNodes.filter((objectNode) =>
            backwardObjectNodes.has(objectNode),
          );

          // Process each of the object nodes that are one hop away.
          oneHopObjectNodes.forEach((hashableNodeId) => {
            if (isInCluster.has(hashableNodeId)) {
              return;
            }
            isInCluster.add(hashableNodeId);
            currentCluster.push([hashableNodeId, distance + 1]);
          });

          // Process each of the account nodes that are two hops away.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          twoHopAccountNodes.forEach((hashableNodeId) => {
            if (isInCluster.has(hashableNodeId)) {
              return;
            }
            queue.push([hashableNodeId, distance + 2]);
            isInCluster.add(hashableNodeId);
            currentCluster.push([hashableNodeId, distance + 2]);
          });
        });

        nextIndexToProcess += 1;
      }

      // Filter by number of hops.
      const hopCountFilteredNodes = currentCluster
        .filter(([_hashableNodeId, distance]) => distance <= queryParams['num-hops'])
        .map(([hashableNodeId, _distance]) => hashableNodeId);

      // NOTE: We skip the cluster size check here, for n-hop.

      hopCountFilteredNodes.forEach((hashableNodeId) => {
        if (queryNodes.length >= queryParams['node-limit']) {
          return;
        }
        if (knownNodes.has(hashableNodeId)) {
          return;
        }
        if (seenInResult.has(hashableNodeId)) {
          return;
        }
        queryNodes.push(hashableNodeId);
        seenInResult.add(hashableNodeId);
      });
    });

    const queryEdges = this.fetchAllEdges(queryNodes, Array.from(knownNodes));
    const seedNodes = queryNodes.filter((hashableNodeId) =>
      matchingAccounts.includes(hashableNodeId),
    );
    return [queryNodes, queryEdges, nextCursor, seedNodes];
  }

  // ----------------------------------------------------------------------------------------------------
  // Backend interface
  // ----------------------------------------------------------------------------------------------------

  private queryResponse(
    queryParams: GraphQueryParams,
    graphId: string,
    queryType: QueryType,
    creatorId: Creator['id'],
    creatorType: Creator['type'],
  ): GraphQuery {
    let immediateNodes: HashableNodeId[] = [];
    let knownNodes: HashableNodeId[] = [];
    let seedNodes: HashableNodeId[] = [];
    let queryEdges: [HashableNodeId, HashableNodeId][] = [];
    let nextCursor: ServerQueryCursor = '';
    switch (queryType) {
      case QueryType.ByAccount: {
        const accountsQuery = queryParams as AccountsQuery;
        [immediateNodes, queryEdges, nextCursor, seedNodes] =
          this.performAccountsQuery(accountsQuery);
        knownNodes = accountsQuery['known-node-ids'].map(nodeIdToHashable);
        break;
      }
      case QueryType.ByCluster: {
        const clustersQuery = queryParams as ClustersQuery;
        [immediateNodes, queryEdges, nextCursor, seedNodes] =
          this.performClustersQuery(clustersQuery);
        knownNodes = clustersQuery['known-node-ids'].map(nodeIdToHashable);
        break;
      }
      case QueryType.NHopsFromAccount: {
        const nHopsQuery = queryParams as NHopsFromAccountQuery;
        [immediateNodes, queryEdges, nextCursor, seedNodes] = this.performNHopsQuery(nHopsQuery);
        knownNodes = nHopsQuery['known-node-ids'].map(nodeIdToHashable);
        break;
      }
      case QueryType.ExperimentalLiveQuery: {
        immediateNodes = this.liveQueryNodes;
        queryEdges = this.liveQueryEdges;
      }
    }

    return {
      type: ApiModel.GraphQuery,
      id: 'grphq_c8U2dBrKtNiMtueGvV5UeGQi',
      attributes: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'cancelled-at': null,
        'completed-at': Date.now().toString(),
        'created-at': Date.now().toString(),
        'creator-id': creatorId,
        'creator-type': creatorType,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'error-cause': null,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'error-message': null,
        'errored-at': Date.now().toString(),
        'graph-id': graphId,
        params: queryParams,
        status: GraphQueryStatus.Completed,
        'query-hint': null,
        'redacted-at': null,
        result: {
          version: 'graph_v2',
          nodes: this.makeServerNodes(immediateNodes),
          edges: queryEdges.map(([hashableNodeId1, hashableNodeId2]) => {
            return this.makeServerEdge(hashableNodeId1, hashableNodeId2);
          }),
          'expandable-nodes': immediateNodes
            .map((hashableNodeId) =>
              this.makeServerNodeExpandability(
                hashableNodeId,
                new Set([...immediateNodes, ...knownNodes]),
              ),
            )
            .filter((item) => item != null),
          'seed-set': seedNodes.map((seedNode) => this.makeServerNode(seedNode)),
          'next-cursor': nextCursor,
        },
        'result-url': 'https://example.com/whatever',
        'timed-out-at': null,
        'updated-at': Date.now().toString(),
      },
      relationships: {
        'graph-query-template': {
          data: null,
        },
        'oldest-snapshot': {
          data: null,
        },
      },
    };
  }

  queryWithTemplate({
    graphQueryTemplateId: _graphQueryTemplateId,
    parameterMap: _parameterMap,
    creatorId,
    creatorType,
    graphId,
  }: Parameters<Backend['queryWithTemplate']>[0]): Promise<GraphQuery> {
    const params: AccountsQuery = {
      type: QueryType.ByAccount,
      conditionals: [
        {
          left: 'account',
          operator: Operator.Equals,
          right: [
            {
              type: ValueType.Text,
              'raw-value': 'act_fake',
              value: 'act_fake',
            },
          ],
        },
      ],
      variables: [],
      'node-limit': 500,
      'known-node-ids': [],
      'expandability-limit': 1,
      'start-cursor': '',
    };
    return new Promise((resolve, _reject) => {
      resolve(this.queryResponse(params, graphId, QueryType.ByAccount, creatorId, creatorType));
    });
  }

  async queryWithTemplateAndPoll({
    graphQueryTemplateId,
    parameterMap,
    graphId,
  }: Parameters<Backend['queryWithTemplateAndPoll']>[0]): Promise<GraphQuery> {
    return await this.queryWithTemplate({
      graphQueryTemplateId,
      parameterMap,
      creatorId: 'demo_creator_id',
      creatorType: 'User',
      graphId,
    });
  }

  async poll(_params: Parameters<Backend['poll']>[0]): Promise<GraphQuery> {
    const params: AccountsQuery = {
      type: QueryType.ByAccount,
      conditionals: [
        {
          left: 'account',
          operator: Operator.Equals,
          right: [
            {
              type: ValueType.Text,
              'raw-value': 'act_fake',
              value: 'act_fake',
            },
          ],
        },
      ],
      variables: [],
      'node-limit': 500,
      'known-node-ids': [],
      'expandability-limit': 1,
      'start-cursor': '',
    };
    return new Promise((resolve, _reject) => {
      resolve(this.queryResponse(params, '1', QueryType.ByAccount, 'demo_creator_id', 'User'));
    });
  }

  async pollContinuously({
    graphQueryId,
  }: Parameters<Backend['pollContinuously']>[0]): Promise<GraphQuery | null> {
    return await this.poll({ graphQueryId });
  }

  async accounts({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['accounts']>[0]): Promise<GraphQuery> {
    return new Promise((resolve, _reject) => {
      resolve(this.queryResponse(query, graphId, QueryType.ByAccount, creatorId, creatorType));
    });
  }

  async cluster({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['cluster']>[0]): Promise<GraphQuery> {
    return new Promise((resolve, _reject) => {
      resolve(this.queryResponse(query, graphId, QueryType.ByCluster, creatorId, creatorType));
    });
  }

  async nHopsFromAccount({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['nHopsFromAccount']>[0]): Promise<GraphQuery> {
    return new Promise((resolve, _reject) => {
      resolve(
        this.queryResponse(query, graphId, QueryType.NHopsFromAccount, creatorId, creatorType),
      );
    });
  }

  async runQueryAndPoll({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['runQueryAndPoll']>[0]) {
    switch (query.type) {
      case QueryType.ByAccount:
        return await this.accounts({ query, graphId, creatorId, creatorType });
      case QueryType.ByCluster:
        return await this.cluster({ query, graphId, creatorId, creatorType });
      case QueryType.NHopsFromAccount:
        return await this.nHopsFromAccount({ query, graphId, creatorId, creatorType });
      default:
        return null;
    }
  }

  async liveQuery(graphId: GraphId): Promise<GraphQuery> {
    if (!this.liveModeEnabled) {
      console.warn('Live query is disabled.');
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return;
    }

    return new Promise((resolve, _reject) => {
      resolve(
        this.queryResponse(
          { type: QueryType.ExperimentalLiveQuery },
          graphId,
          QueryType.ExperimentalLiveQuery,
          'demo_creator_id',
          'User',
        ),
      );

      this.liveQueryIndex += 1;
      // TODO: Maybe reset live query index separately
      if (this.liveQueryIndex === this.liveQueryNodeSets.length) {
        this.liveQueryIndex = 0;
      }
    });
  }

  customDatumTypesOverride(): ServerNodeDatumType[] {
    const customDatumTypesList: ServerNodeDatumType[] = Array.from(
      this.customDatumTypesByToken.values(),
    );
    return customDatumTypesList;
  }

  propertyConfigOverride(): ServerPropertyConfig[] {
    return this.propertyConfigs;
  }

  defaultRenderConfigs: ServerNodeRenderConfig[] = [
    {
      'datum-type-token': ACCOUNT_DATUM_TYPE.token,
      'display-name': 'Account',
      color: '#631fff',
      icon: NodeDataTypeIcon.Account,
      'search-placeholder': '',
      'context-menu': [
        {
          source: PropertySource.Persona,
          value: PersonaPropertyValues.Name,
        },
        {
          source: PropertySource.Persona,
          value: PersonaPropertyValues.ReferenceId,
        },
        {
          source: PropertySource.Persona,
          value: PersonaPropertyValues.Status,
        },
        {
          source: PropertySource.Persona,
          value: PersonaPropertyValues.NumberOfInquiries,
        },
        {
          source: PropertySource.Persona,
          value: PersonaPropertyValues.Tags,
        },
        {
          source: PropertySource.Persona,
          value: PersonaPropertyValues.CreatedAt,
        },
      ],
    },
    {
      'datum-type-token': ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE.token,
      'display-name': 'Similarity Level',
      color: '#3DD6D6',
      icon: NodeDataTypeIcon.Smile,
      'search-placeholder': '',
      'context-menu': [],
    },
    {
      'datum-type-token': ADDRESS_DATUM_TYPE.token,
      'display-name': 'Address',
      color: '#FABEB2',
      icon: NodeDataTypeIcon.Address,
      'search-placeholder': '',
      'context-menu': [],
    },
    {
      'datum-type-token': BIRTHDATE_DATUM_TYPE.token,
      'display-name': 'Birthdate',
      color: '#A3E1BC',
      icon: NodeDataTypeIcon.Calendar,
      'search-placeholder': 'e.g. 1970-01-27',
      'context-menu': [],
    },
    {
      'datum-type-token': BROWSER_FINGERPRINT_DATUM_TYPE.token,
      'display-name': 'Browser Fingerprint',
      color: '#87EAEA',
      icon: NodeDataTypeIcon.Fingerprint,
      'search-placeholder': 'e.g. a07cc 1f7e7abc6515fa932b4cdf12b11',
      'context-menu': [],
    },
    {
      'datum-type-token': DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
      'display-name': 'Device Fingerprint',
      color: '#1BB8B8',
      icon: NodeDataTypeIcon.Fingerprint,
      'search-placeholder': 'e.g. PTW93ZArKL8c3v4hVUMV',
      'context-menu': [],
    },
    {
      'datum-type-token': DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE.token,
      'display-name': 'Device Vendor ID',
      color: '#7CD49F',
      icon: NodeDataTypeIcon.Fingerprint,
      'search-placeholder': 'e.g. 7bab59c4-6be9-4bc9-ba8f-e3f8a130701f',
      'context-menu': [],
    },
    {
      'datum-type-token': DEVICE_TOKEN_DATUM_TYPE.token,
      'display-name': 'Device Token',
      color: '#033682',
      icon: NodeDataTypeIcon.Fingerprint,
      'search-placeholder': 'e.g. dev_zVbXMCv5oJioB4zkygCWetbT',
      'context-menu': [],
    },
    {
      'datum-type-token': EMAIL_DATUM_TYPE.token,
      'display-name': 'Email Address',
      color: '#FFDE85',
      icon: NodeDataTypeIcon.Email,
      'search-placeholder': 'e.g. username@; @example.com; username@example.com',
      'context-menu': [],
    },
    {
      'datum-type-token': GOVERNMENT_ID_DATUM_TYPE.token,
      'display-name': 'Government ID Number',
      color: '#FABEB2',
      icon: NodeDataTypeIcon.Inquiries,
      'search-placeholder': '',
      'context-menu': [],
    },
    {
      'datum-type-token': ID_DOCUMENT_NUMBER_DATUM_TYPE.token,
      'display-name': 'Government ID Document Number',
      color: '#735A5A',
      icon: NodeDataTypeIcon.Inquiries,
      'search-placeholder': 'e.g. 123456789',
      'context-menu': [],
    },
    {
      'datum-type-token': NAME_DATUM_TYPE.token,
      'display-name': 'Name',
      color: '#51E3E3',
      icon: NodeDataTypeIcon.Name,
      'search-placeholder': 'e.g. John; Smith; John Smith',
      'context-menu': [],
    },
    {
      'datum-type-token': NETWORK_DATUM_TYPE.token,
      'display-name': 'IP Address',
      color: '#56C883',
      icon: NodeDataTypeIcon.Globe,
      'search-placeholder': 'e.g. 127.0.0.1',
      'context-menu': [],
    },
    {
      'datum-type-token': PHONE_NUMBER_DATUM_TYPE.token,
      'display-name': 'Phone Number',
      color: '#FEB9CB',
      icon: NodeDataTypeIcon.Phone,
      'search-placeholder': 'e.g. 1 201 555-1212; +44 7700 900077',
      'context-menu': [],
    },
    {
      'datum-type-token': POSTAL_CODE_DATUM_TYPE.token,
      'display-name': 'Postal Code',
      color: '#FFC31F',
      icon: NodeDataTypeIcon.Postal,
      'search-placeholder': 'e.g. 90210; 90210+1234; H4A 1A1',
      'context-menu': [],
    },
    {
      'datum-type-token': SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
      'display-name': 'SSN',
      color: '#5598FC',
      icon: NodeDataTypeIcon.Inquiries,
      'search-placeholder': 'e.g. 123456789',
      'context-menu': [],
    },
    {
      'datum-type-token': SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token,
      'display-name': 'Last 4 SSN',
      color: '#2379FA',
      icon: NodeDataTypeIcon.Inquiries,
      'search-placeholder': 'e.g. 6789',
      'context-menu': [],
    },
    // Transactions Demo datum types.
    {
      'datum-type-token': EXTERNAL_ACCOUNT_DATUM_TYPE.token,
      'display-name': 'External account',
      color: '#2379FA',
      icon: NodeDataTypeIcon.Account,
      'search-placeholder': 'e.g. [external_account_id]',
      'context-menu': [],
    },
    {
      'datum-type-token': TRANSACTIONS_DATUM_TYPE.token,
      'display-name': 'Transactions',
      color: '#35A260',
      icon: NodeDataTypeIcon.Transaction,
      'search-placeholder': 'e.g. [transaction ID]',
      'context-menu': [],
    },
  ];

  nodeRenderConfigOverride(): ServerNodeRenderConfig[] {
    return this.defaultRenderConfigs.concat(this.nodeRenderConfigs).filter(Boolean);
  }

  coloringRulesOverride(): ServerColorRule[] {
    return this.coloringRules;
  }

  async expand({
    nodeId,
    expandDatumTypes,
    existingNodeIds,
  }: Parameters<Backend['expand']>[0]): Promise<ServerExpandResponse> {
    const hashableNodeId = nodeIdToHashable(nodeId);
    const existingHashableNodeIds: HashableNodeId[] = existingNodeIds.map(nodeIdToHashable);

    const acceptableDatumTypes: Set<DatumTypeToken> = new Set();
    expandDatumTypes.forEach((datumTypeToken) => {
      acceptableDatumTypes.add(datumTypeToken);
    });

    const neighboringHashableNodeIds: HashableNodeId[] = [];
    const nodes: ServerNode[] = [];
    const edges: ServerEdge[] = [];

    // Do one hop from the node.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId).forEach((neighborHashableNodeId) => {
      if (existingHashableNodeIds.includes(neighborHashableNodeId)) {
        return;
      }
      const neighborNodeId = nodeIdFromHashable(neighborHashableNodeId);
      if (acceptableDatumTypes.has(neighborNodeId.token)) {
        neighboringHashableNodeIds.push(neighborHashableNodeId);
        nodes.push(this.makeServerNode(neighborHashableNodeId));
        edges.push(this.makeServerEdge(hashableNodeId, neighborHashableNodeId));
      }
    });

    // Check for latent edges between existing nodes and the newly revealed nodes from the expansion.
    neighboringHashableNodeIds.forEach((neighboringHashableNodeId) => {
      const twoHopNodeIdSet = this.adjacencyLists.get(neighboringHashableNodeId);
      const intersection = existingHashableNodeIds.filter(
        (existingHashableNodeId) =>
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          twoHopNodeIdSet.has(existingHashableNodeId) && existingHashableNodeId !== hashableNodeId,
      );
      intersection.forEach((twoHopHashableNodeId) => {
        edges.push(this.makeServerEdge(neighboringHashableNodeId, twoHopHashableNodeId));
      });
    });

    // Compute which of the new nodes are expandable.
    const allKnownNodes: Set<HashableNodeId> = new Set([
      ...existingHashableNodeIds,
      ...neighboringHashableNodeIds,
    ]);
    const expandableNodes: ServerNodeExpandability[] = neighboringHashableNodeIds
      .map((neighboringHashableNodeId) =>
        this.makeServerNodeExpandability(neighboringHashableNodeId, allKnownNodes),
      )
      .filter((item) => item != null);

    const serverExpandResponse: ServerExpandResponse = {
      version: 'graph_v2',
      'nodes-and-edges': {
        nodes,
        edges,
      },
      'expandable-nodes': expandableNodes,
    };

    return new Promise((resolve, _reject) => {
      setTimeout(() => {
        resolve(serverExpandResponse);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      }, Math.random() * window.DEMO_BACKEND_EXPANSION_DELAY_BASE);
    });
  }

  async hydrate({ nodeIds }: Parameters<Backend['hydrate']>[0]): Promise<ServerHydrateResponse> {
    const hashableNodeIds = nodeIds.map((nodeId) => {
      return nodeIdToHashable(nodeId);
    });
    const serverHydrateResponse: ServerHydrateResponse = {
      version: 'graph_v2',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'hydrated-nodes': hashableNodeIds.map((hashableNodeId) => {
        return this.nodeHydration.get(hashableNodeId);
      }),
    };
    return new Promise((resolve, _reject) => {
      resolve(serverHydrateResponse);
    });
  }

  async lightlyHydrate({
    nodeIds,
  }: Parameters<Backend['lightlyHydrate']>[0]): Promise<ServerLightlyHydrateResponse> {
    const lightlyHydratedNodes: ServerLightlyHydratedNode[] = nodeIds.map((nodeId) => {
      const hydratedNode = this.nodeHydration.get(nodeIdToHashable(nodeId));
      switch (nodeId.token) {
        case ACCOUNT_DATUM_TYPE.token: {
          const hydratedAccount = hydratedNode as ServerHydratedAccount;
          const lightlyHydratedAccount: ServerLightlyHydratedAccount = {
            status: hydratedAccount.status,
            tags: hydratedAccount.tags,
            'has-completed-inquiries': hydratedAccount['has-completed-inquiries'],
            'transaction-account-info': hydratedAccount['transaction-account-info'],
          };
          return lightlyHydratedAccount;
        }
        case EXTERNAL_ACCOUNT_DATUM_TYPE.token: {
          const hydratedExternalAccount = hydratedNode as ServerHydratedExternalAccount;
          const lightlyHydratedExternalAccount: ServerLightlyHydratedExternalAccount = {
            'transaction-account-info': hydratedExternalAccount['transaction-account-info'],
          };
          return lightlyHydratedExternalAccount;
        }
        case TRANSACTIONS_DATUM_TYPE.token: {
          const hydratedTransactions = hydratedNode as ServerHydratedTransactions;
          const lightlyHydratedTransactions: ServerLightlyHydratedTransactions = {
            account1: hydratedTransactions.account1,
            account2: hydratedTransactions.account2,
            netVolume: hydratedTransactions.netVolume,
          };
          return lightlyHydratedTransactions;
        }
        default: {
          return {};
        }
      }
    });

    const serverLightlyHydrateResponse: ServerLightlyHydrateResponse = {
      version: 'graph_v2',
      'lightly-hydrated-nodes': lightlyHydratedNodes,
    };
    return new Promise((resolve, _reject) => {
      resolve(serverLightlyHydrateResponse);
    });
  }

  // ----------------------------------------------------------------------------------------------------
  // Interface for setting the query results
  // ----------------------------------------------------------------------------------------------------

  clearNodesAndEdges() {
    this.nodes.clear();
    this.edges.clear();
    this.adjacencyLists.clear();
    this.nodeHydration.clear();
  }

  private removeNodesAndEdges(nodes: HashableNodeId[], edges: [HashableNodeId, HashableNodeId][]) {
    nodes.forEach((hashableNodeId) => {
      this.nodes.delete(hashableNodeId);
      this.adjacencyLists.delete(hashableNodeId);
      this.nodeHydration.delete(hashableNodeId);
    });
    edges.forEach(([hashableNodeId1, hashableNodeId2]) => {
      const hashableNodeIdPair = this.makeHashableNodeIdPair(hashableNodeId1, hashableNodeId2);
      this.edges.delete(hashableNodeIdPair);
    });
  }

  private ingestQueryResult(
    nodes: Map<HashableNodeId, ServerHydratedNode>,
    edges: [HashableNodeId, HashableNodeId][],
    _immediateNodes: HashableNodeId[],
  ) {
    // Validate the inputs, to make sure everything seems reasonable.
    nodes.forEach((_hydratedNode, hashableNodeId) => {
      if (this.nodes.has(hashableNodeId)) {
        console.warn(`Node ${hashableNodeId} already exists.`);
      }
    });
    edges.forEach(([hashableNodeId1, hashableNodeId2]) => {
      if (!nodes.has(hashableNodeId1)) {
        console.warn(`Node ${hashableNodeId1} does not exist.`);
      }
      if (!nodes.has(hashableNodeId2)) {
        console.warn(`Node ${hashableNodeId2} does not exist.`);
      }
    });

    nodes.forEach((hydratedNode, hashableNodeId) => {
      this.nodes.set(hashableNodeId, this.makeServerNode(hashableNodeId));
      this.adjacencyLists.set(hashableNodeId, new Set());
      this.nodeHydration.set(hashableNodeId, hydratedNode);
    });
    edges.forEach(([hashableNodeId1, hashableNodeId2]) => {
      const hashableNodeIdPair = this.makeHashableNodeIdPair(hashableNodeId1, hashableNodeId2);
      this.edges.set(hashableNodeIdPair, this.makeServerEdge(hashableNodeId1, hashableNodeId2));
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.adjacencyLists.get(hashableNodeId1).add(hashableNodeId2);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.adjacencyLists.get(hashableNodeId2).add(hashableNodeId1);
    });
  }

  private addNode(hashableNodeId: HashableNodeId, hydratedNode: ServerHydratedNode) {
    if (
      this.nodes.has(hashableNodeId) ||
      this.adjacencyLists.has(hashableNodeId) ||
      this.nodeHydration.has(hashableNodeId)
    ) {
      throw new Error(`node ${hashableNodeId} already exists`);
    }

    this.nodes.set(hashableNodeId, this.makeServerNode(hashableNodeId));
    this.adjacencyLists.set(hashableNodeId, new Set());
    this.nodeHydration.set(hashableNodeId, hydratedNode);
  }

  // NOTE: If the edge is already present, the result is effectively a no-op.
  addEdge(hashableNodeId1: HashableNodeId, hashableNodeId2: HashableNodeId) {
    const hashableNodeIdPair = this.makeHashableNodeIdPair(hashableNodeId1, hashableNodeId2);
    this.edges.set(hashableNodeIdPair, this.makeServerEdge(hashableNodeId1, hashableNodeId2));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId1).add(hashableNodeId2);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.adjacencyLists.get(hashableNodeId2).add(hashableNodeId1);
  }

  // Nodes and edges will be presented in sets
  setLiveQueryResults(
    results: Array<[Map<HashableNodeId, ServerHydratedNode>, [HashableNodeId, HashableNodeId][]]>,
  ) {
    const nodes = this.liveQueryNodeSets.flat();
    const edges = this.liveQueryEdgeSets.flat();

    // Drop all of the nodes and edges from the last call.
    this.removeNodesAndEdges(nodes, edges);

    // Set the data.
    results.forEach((result) => {
      const [nodes, edges] = result;
      this.ingestQueryResult(nodes, edges, []);
    });

    this.liveQueryNodeSets = results.map((set) => Array.from(set[0].keys()));
    this.liveQueryEdgeSets = results.map((set) => set[1]);
  }

  setCustomDatumTypes(customDatumTypes: ServerNodeDatumType[]) {
    this.customDatumTypesByToken.clear();
    customDatumTypes.forEach((customDatumType) => {
      this.customDatumTypesByToken.set(customDatumType['datum-type-token'], customDatumType);
    });
  }

  setColoringRules(newColoringRules: ServerColorRule[]) {
    this.coloringRules = newColoringRules;
  }

  setGraphRenderConfigs(newNodeRenderConfigs: ServerNodeRenderConfig[]) {
    this.nodeRenderConfigs = newNodeRenderConfigs;
  }

  // ----------------------------------------------------------------------------------------------------
  // Reading and writing to LocalStorage
  // ----------------------------------------------------------------------------------------------------

  private loadDataFromJson(payload: string) {
    const localStorageData: DemoBackendLocalStorage = JSON.parse(payload);
    if (localStorageData == null) {
      // Exit early if there's no data to set.
      return;
    }
    this.customDatumTypesByToken = new Map(
      localStorageData.customDatumTypes.map((datumType) => {
        return [datumType['datum-type-token'], datumType];
      }),
    );
    this.coloringRules = localStorageData.coloringRules;
    this.customDatumTypeCounter = localStorageData.customDatumTypeCounter;
    this.nodes = new Map(localStorageData.nodes);
    this.edges = new Map(localStorageData.edges);
    this.adjacencyLists = new Map(
      localStorageData.adjacencyLists.map(([hashableNodeId, neighborHashableNodeIds]) => {
        return [hashableNodeId, new Set(neighborHashableNodeIds)];
      }),
    );
    this.nodeHydration = new Map(localStorageData.nodeHydration);
    this.nodeRenderConfigs = localStorageData.nodeRenderConfigs;

    this.liveQueryNodeSets = localStorageData.liveQueryNodeSets;
    this.liveQueryEdgeSets = localStorageData.liveQueryEdgeSets;

    this.nodeIdCounter = localStorageData.nodeIdCounter;
  }

  readFromLocalStorage() {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.loadDataFromJson(window.localStorage.getItem(GraphExplorerMockDataKey.DemoBackend));
  }

  writeToLocalStorage() {
    const localStorageData: DemoBackendLocalStorage = {
      customDatumTypes: Array.from(this.customDatumTypesByToken.values()),
      nodeRenderConfigs: Array.from(this.nodeRenderConfigs),
      coloringRules: Array.from(this.coloringRules),
      customDatumTypeCounter: this.customDatumTypeCounter,
      nodes: Array.from(this.nodes.entries()),
      edges: Array.from(this.edges.entries()),
      adjacencyLists: Array.from(this.adjacencyLists.entries()).map(
        ([hashableNodeId, neighborHashableNodeIds]) => {
          return [hashableNodeId, Array.from(neighborHashableNodeIds.values())];
        },
      ),
      nodeHydration: Array.from(this.nodeHydration.entries()),

      liveQueryNodeSets: this.liveQueryNodeSets,
      liveQueryEdgeSets: this.liveQueryEdgeSets,

      nodeIdCounter: this.nodeIdCounter,
    };
    window.localStorage.setItem(
      GraphExplorerMockDataKey.DemoBackend,
      JSON.stringify(localStorageData),
    );
  }

  loadFromServer(payload: string) {
    this.loadDataFromJson(payload);
  }

  // ----------------------------------------------------------------------------------------------------
  // Convenience functions for constructing payloads
  // ----------------------------------------------------------------------------------------------------

  static readonly NODE_TYPES = DemoBackendNodeType;
  static readonly VALUE_TYPES = DemoBackendValueType;
  static readonly ICON_TYPES = NodeDataTypeIcon;

  newCustomDatumType(
    name: string,
    displayName: string,
    valueType: DatumValueType,
    colorString: string,
    iconEnum: NodeDataTypeIcon,
  ): [ServerNodeDatumType, ServerNodeRenderConfig] {
    const nodeDatumType: ServerNodeDatumType = {
      'datum-type-token': `gdt_${this.customDatumTypeCounter}`,
      name,
      'value-type': valueType,
    };
    const nodeRenderConfig: ServerNodeRenderConfig = {
      'datum-type-token': `gdt_${this.customDatumTypeCounter}`,
      'display-name': displayName,
      color: colorString,
      icon: iconEnum,
      'search-placeholder': '',
      'context-menu': [],
    };
    this.customDatumTypeCounter += 1;
    return [nodeDatumType, nodeRenderConfig];
  }

  newHashableNodeId(
    nodeType: DemoBackendNodeType,
    nodeDisplayValue: string | null = null,
  ): HashableNodeId {
    let datumType: DatumType = ACCOUNT_DATUM_TYPE;
    let computedNodeValue: ParameterMap;
    switch (nodeType) {
      case DemoBackendNodeType.Account:
        datumType = ACCOUNT_DATUM_TYPE;
        break;
      case DemoBackendNodeType.AccountStatus:
        datumType = ACCOUNT_STATUS_DATUM_TYPE;
        break;
      case DemoBackendNodeType.AccountSimilaritySelfie:
        datumType = ACCOUNT_SIMILARITY_SELFIE_DATUM_TYPE;
        break;
      case DemoBackendNodeType.AccountType:
        datumType = ACCOUNT_TYPE_DATUM_TYPE;
        break;
      case DemoBackendNodeType.Address:
        datumType = ADDRESS_DATUM_TYPE;
        if (nodeDisplayValue) {
          computedNodeValue = {};
          nodeDisplayValue.split(',').forEach((addressLine, i) => {
            computedNodeValue[`address_line${i + 1}`] = addressLine;
          });
        }
        break;
      case DemoBackendNodeType.Birthdate:
        datumType = BIRTHDATE_DATUM_TYPE;
        break;
      case DemoBackendNodeType.BrowserFingerprint:
        datumType = BROWSER_FINGERPRINT_DATUM_TYPE;
        break;
      case DemoBackendNodeType.DeviceFingerprintBrowser:
        datumType = DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE;
        break;
      case DemoBackendNodeType.DeviceFingerprintMobileSdk:
        datumType = DEVICE_FINGERPRINT_MOBILE_SDK_DATUM_TYPE;
        break;
      case DemoBackendNodeType.DeviceToken:
        datumType = DEVICE_TOKEN_DATUM_TYPE;
        break;
      case DemoBackendNodeType.Email:
        datumType = EMAIL_DATUM_TYPE;
        break;
      case DemoBackendNodeType.GovernmentId:
        datumType = GOVERNMENT_ID_DATUM_TYPE;
        break;
      case DemoBackendNodeType.IdDocumentNumber: {
        datumType = ID_DOCUMENT_NUMBER_DATUM_TYPE;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const tokens = nodeDisplayValue.split(' ');
        if (tokens.length !== 3) {
          throw new Error(
            'Expected a display value of: "[id_document_number] [issuing_country] [issuing_subdivision]"',
          );
        }
        computedNodeValue = {
          'document-number': tokens[0],
          'issuing-country': tokens[1],
          'issuing-subdivision': tokens[2],
        };
        break;
      }
      case DemoBackendNodeType.IdNumber:
        datumType = ID_NUMBER_DATUM_TYPE;
        break;
      case DemoBackendNodeType.Name:
        datumType = NAME_DATUM_TYPE;
        computedNodeValue = {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'name-first': nodeDisplayValue.split(' ').shift(),
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'name-last': nodeDisplayValue.split(' ').splice(1)[0],
        };
        break;
      case DemoBackendNodeType.Network:
        datumType = NETWORK_DATUM_TYPE;
        break;
      case DemoBackendNodeType.PhoneNumber:
        datumType = PHONE_NUMBER_DATUM_TYPE;
        break;
      case DemoBackendNodeType.PostalCode:
        datumType = POSTAL_CODE_DATUM_TYPE;
        break;
      case DemoBackendNodeType.Ssn:
        datumType = SOCIAL_SECURITY_NUMBER_DATUM_TYPE;
        break;
      case DemoBackendNodeType.SsnLast4:
        datumType = SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE;
        break;
      case DemoBackendNodeType.ExternalAccount:
        datumType = EXTERNAL_ACCOUNT_DATUM_TYPE;
        break;
      case DemoBackendNodeType.Transactions:
        datumType = TRANSACTIONS_DATUM_TYPE;
        break;
      default:
        return assertNever(nodeType);
    }
    const nodeId: NodeId = {
      token: datumType.token,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      value: `${JSON.stringify(computedNodeValue) || nodeDisplayValue || this.nodeIdCounter}`,
    };
    this.nodeIdCounter += 1;
    return nodeIdToHashable(nodeId);
  }

  newCustomDatumTypeHashableNodeId(
    customDatumType: ServerNodeDatumType,
    nodeDisplayValue: string | null = null,
  ): HashableNodeId {
    const nodeId: NodeId = {
      token: customDatumType['datum-type-token'],
      value: `${nodeDisplayValue || this.nodeIdCounter}`,
    };
    this.nodeIdCounter += 1;
    return nodeIdToHashable(nodeId);
  }

  addAccount({
    accountToken,
    referenceId = '',
    createdAt = DateTime.local().minus({ days: 14 }).startOf('day').toISO(),
    status = ACTIVE_ACCOUNT_STATUS,
    tags = [],
    selfies = [],
    firstName = '',
    middleName = '',
    lastName = '',
    numberOfInquiriesDeclined = 0,
    hasCompletedInquiries = true,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    transactionAccountInfo = undefined,
  }: {
    accountToken: AccountToken;
    createdAt: string;
    referenceId: ReferenceId;
    status: string;
    tags: Tag[];
    selfies: SelfieFile[];
    firstName: string;
    middleName: string;
    lastName: string;
    numberOfInquiriesDeclined: number;
    hasCompletedInquiries: boolean;
    transactionAccountInfo: TransactionAccountInfo;
  }): HashableNodeId {
    const account = this.newHashableNodeId(DemoBackend.NODE_TYPES.Account);
    const hydratedAccount: ServerHydratedAccount = {
      token: accountToken,
      status: status,
      'created-at': createdAt,
      'reference-id': referenceId,
      tags,
      name: {
        first: firstName,
        middle: middleName,
        last: lastName,
      },
      selfies: selfies,
      'inquiry-counts': {
        approved: 0,
        declined: numberOfInquiriesDeclined,
        'needs-review': 0,
        completed: 0,
        failed: 0,
        expired: 0,
        created: 0,
        pending: 0,
      },
      'has-completed-inquiries': hasCompletedInquiries,
      properties: [],
      'transaction-account-info': transactionAccountInfo,
      'fields-data': null,
    };
    this.addNode(account, hydratedAccount);
    return account;
  }

  addAccountStatus(status: string): HashableNodeId {
    const accountStatus = this.newHashableNodeId(DemoBackend.NODE_TYPES.AccountStatus, status);
    const hydratedAccountStatus: ServerHydratedAccountStatus = {
      status,
    };
    this.addNode(accountStatus, hydratedAccountStatus);
    return accountStatus;
  }

  addAccountType(type: string): HashableNodeId {
    const accountType = this.newHashableNodeId(DemoBackend.NODE_TYPES.AccountType, type);
    const hydratedAccountType: ServerHydratedAccountType = {
      type,
    };
    this.addNode(accountType, hydratedAccountType);
    return accountType;
  }

  addAddress(
    displayValue: string,
    addressLine1: string,
    addressLine2: string,
    addressLine3: string,
    countryCode: string,
  ): HashableNodeId {
    const address = this.newHashableNodeId(DemoBackend.NODE_TYPES.Address, displayValue);
    const hydratedAddress: ServerHydratedAddress = {
      'address-line1': addressLine1,
      'address-line2': addressLine2,
      'address-line3': addressLine3,
      'country-code': countryCode,
    };
    this.addNode(address, hydratedAddress);
    return address;
  }

  addBirthdate(birthdate: Iso8601DateString): HashableNodeId {
    const birthdateNode = this.newHashableNodeId(DemoBackend.NODE_TYPES.Birthdate, birthdate);
    const hydratedBirthdate: ServerHydratedBirthdate = {
      birthdate,
    };
    this.addNode(birthdateNode, hydratedBirthdate);
    return birthdateNode;
  }

  addBrowserFingerprint(fingerprint: BrowserFingerprint): HashableNodeId {
    const browserFingerprint = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.BrowserFingerprint,
      fingerprint,
    );
    const hydratedBrowserFingerprint: ServerHydratedBrowserFingerprint = {
      'browser-fingerprint': fingerprint,
      'browser-names': [],
    };
    this.addNode(browserFingerprint, hydratedBrowserFingerprint);
    return browserFingerprint;
  }

  addDeviceFingerprintBrowser(fingerprint: DeviceFingerprintBrowser): HashableNodeId {
    const deviceFingerprint = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.DeviceFingerprintBrowser,
      fingerprint,
    );
    const hydratedDeviceFingerprint: ServerHydratedDeviceFingerprint = {
      'device-fingerprint': fingerprint,
      'device-types': [],
    };
    this.addNode(deviceFingerprint, hydratedDeviceFingerprint);
    return deviceFingerprint;
  }

  addDeviceToken(token: string): HashableNodeId {
    const deviceToken = this.newHashableNodeId(DemoBackend.NODE_TYPES.DeviceToken, token);
    const hydratedDeviceToken: ServerHydratedDeviceToken = {
      token,
    };
    this.addNode(deviceToken, hydratedDeviceToken);
    return deviceToken;
  }

  addDeviceFingerprintMobileSdk(fingerprint: DeviceFingerprintMobileSdk): HashableNodeId {
    const deviceFingerprint = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.DeviceFingerprintMobileSdk,
      fingerprint,
    );
    const hydratedDeviceVendorId: ServerHydratedDeviceVendorId = {
      'device-vendor-id': deviceFingerprint,
    };
    this.addNode(deviceFingerprint, hydratedDeviceVendorId);
    return deviceFingerprint;
  }

  addEmail(email: EmailAddress): HashableNodeId {
    const emailNode = this.newHashableNodeId(DemoBackend.NODE_TYPES.Email, email);
    const hydratedEmail: ServerHydratedEmail = {
      email,
    };
    this.addNode(emailNode, hydratedEmail);
    return emailNode;
  }

  addGovernmentId(governmentId: string): HashableNodeId {
    const governmentIdNode = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.GovernmentId,
      governmentId,
    );
    const hydratedGovernmentId: ServerHydratedGovernmentId = {
      id: governmentId,
    };
    this.addNode(governmentIdNode, hydratedGovernmentId);
    return governmentIdNode;
  }

  addIdDocumentNumber(
    displayValue: string,
    documentNumber: string,
    issuingCountry: string,
    issuingSubdivision: string,
  ): HashableNodeId {
    const idDocumentNumber = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.IdDocumentNumber,
      displayValue,
    );
    const hydratedDocumentNumber: ServerHydratedIdDocumentNumber = {
      'document-number': documentNumber,
      'issuing-country': issuingCountry,
      'issuing-subdivision': issuingSubdivision,
    };
    this.addNode(idDocumentNumber, hydratedDocumentNumber);
    return idDocumentNumber;
  }

  addIdNumber(idNumber: string): HashableNodeId {
    const idNumberNode = this.newHashableNodeId(DemoBackend.NODE_TYPES.IdNumber, idNumber);
    const hydratedIdNumber: ServerHydratedIdNumber = {
      id: idNumber,
    };
    this.addNode(idNumberNode, hydratedIdNumber);
    return idNumberNode;
  }

  addName(displayValue: string, nameFirst: Name, nameMiddle: Name, nameLast: Name): HashableNodeId {
    const name = this.newHashableNodeId(DemoBackend.NODE_TYPES.Name, displayValue);
    const hydratedName: ServerHydratedName = {
      'name-first': nameFirst,
      'name-middle': nameMiddle,
      'name-last': nameLast,
    };
    this.addNode(name, hydratedName);
    return name;
  }

  addNetwork(ipAddress: IpAddress): HashableNodeId {
    const network = this.newHashableNodeId(DemoBackend.NODE_TYPES.Network, ipAddress);
    const hydratedNetwork: ServerHydratedNetwork = {
      ip: ipAddress,
      'proxy-type': '',
      'region-name': '',
      'country-name': '',
      'threat-level': '',
    };
    this.addNode(network, hydratedNetwork);
    return network;
  }

  addPhoneNumber(phone: PhoneNumber): HashableNodeId {
    const phoneNumber = this.newHashableNodeId(DemoBackend.NODE_TYPES.PhoneNumber, phone);
    const hydratedPhoneNumber: ServerHydratedPhoneNumber = {
      phone,
    };
    this.addNode(phoneNumber, hydratedPhoneNumber);
    return phoneNumber;
  }

  addPostalCode(postalCode: PostalCode): HashableNodeId {
    const postalCodeNode = this.newHashableNodeId(DemoBackend.NODE_TYPES.PostalCode, postalCode);
    const hydratedPostalCode: ServerHydratedPostalCode = {
      'postal-code': postalCode,
    };
    this.addNode(postalCodeNode, hydratedPostalCode);
    return postalCodeNode;
  }

  addAccountSimilaritySelfie(
    displayValue: string,
    details: {
      account1: {
        id: AccountToken;
        selfies: SelfieFile[];
      };
      account2: {
        id: AccountToken;
        selfies: SelfieFile[];
      };
      confidence: number;
    },
  ) {
    const accountSimilaritySelfie = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.AccountSimilaritySelfie,
      displayValue,
    );
    const hydratedAccountSimilaritySelfie: ServerHydratedAccountSimilaritySelfie = details;
    this.addNode(accountSimilaritySelfie, hydratedAccountSimilaritySelfie);
    return accountSimilaritySelfie;
  }

  addSelfieSimilarity(
    displayValue: string,
    details: {
      'account-selfies': {
        'account-id': AccountToken;
        selfies: SelfieFile[];
      }[];
      confidence: number;
    },
  ): HashableNodeId {
    const transformedDetails = {
      account1: {
        id: details['account-selfies'][0]['account-id'],
        selfies: details['account-selfies'][0]['selfies'],
      },
      account2: {
        id: details['account-selfies'][1]['account-id'],
        selfies: details['account-selfies'][1]['selfies'],
      },
      confidence: details['confidence'],
    };

    return this.addAccountSimilaritySelfie(displayValue, transformedDetails);
  }

  addEmailSimilarity(
    datumType: ServerNodeDatumType,
    details: {
      account1: {
        id: AccountToken;
        email: string;
      };
      account2: {
        id: AccountToken;
        email: string;
      };
    },
    value: string,
    criteria: EmailSimCriteria[],
  ) {
    const emailSimilarity = this.newCustomDatumTypeHashableNodeId(datumType, value);
    const hydratedEmailSimilarity: ServerHydratedEmailSim = {
      'display-name': datumType.name,
      criteria: criteria,
      details: details,
    };
    this.addNode(emailSimilarity, hydratedEmailSimilarity);
    return emailSimilarity;
  }

  addImageBackgroundSimilarity(
    datumType: ServerNodeDatumType,
    details: {
      account1: {
        id: AccountToken;
        images: SelfieFile[];
      };
      account2: {
        id: AccountToken;
        images: SelfieFile[];
      };
      similarityScore: number;
    },
    value: string,
    criteria: ImageBackgroundSimCriteria[],
  ) {
    const imageBackgroundSimilarity = this.newCustomDatumTypeHashableNodeId(datumType, value);
    const hydratedImageBackgroundSimilarity: ServerHydratedImageBackgroundSim = {
      'display-name': datumType.name,
      criteria: criteria,
      details: details,
    };
    this.addNode(imageBackgroundSimilarity, hydratedImageBackgroundSimilarity);
    return imageBackgroundSimilarity;
  }

  addSsn(ssn: Ssn): HashableNodeId {
    const ssnNode = this.newHashableNodeId(DemoBackend.NODE_TYPES.Ssn, ssn);
    const hydratedSsn: ServerHydratedSsn = {
      ssn,
    };
    this.addNode(ssnNode, hydratedSsn);
    return ssnNode;
  }

  addSsLast4(ssnLast4: SsnLast4): HashableNodeId {
    const ssnLast4Node = this.newHashableNodeId(DemoBackend.NODE_TYPES.SsnLast4, ssnLast4);
    const hydratedSsnLast4: ServerHydratedSsnLast4 = {
      'ssn-last4': ssnLast4,
    };
    this.addNode(ssnLast4Node, hydratedSsnLast4);
    return ssnLast4Node;
  }

  addExternalAccount(transactionAccountInfo: TransactionAccountInfo): HashableNodeId {
    const externalAccountNode = this.newHashableNodeId(
      DemoBackend.NODE_TYPES.ExternalAccount,
      `${transactionAccountInfo['routing-number']}|${transactionAccountInfo['account-number']}`,
    );
    const hydratedExternalAccount: ServerHydratedExternalAccount = {
      'transaction-account-info': transactionAccountInfo,
    };
    this.addNode(externalAccountNode, hydratedExternalAccount);
    return externalAccountNode;
  }

  addTransactions(
    transactionAccountInfo1: TransactionAccountInfo,
    transactionAccountInfo2: TransactionAccountInfo,
    transactions: TransactionData[],
    netVolume: number,
  ): HashableNodeId {
    const transactionsNode = this.newHashableNodeId(DemoBackend.NODE_TYPES.Transactions);
    const hydratedTransactions: ServerHydratedTransactions = {
      account1: transactionAccountInfo1,
      account2: transactionAccountInfo2,
      transactions,
      netVolume,
    };
    this.addNode(transactionsNode, hydratedTransactions);
    return transactionsNode;
  }

  addCustomNode(datumType: ServerNodeDatumType, value: string): HashableNodeId {
    const customNode = this.newCustomDatumTypeHashableNodeId(datumType, value);
    const hydratedCustomNode: ServerHydratedCustomDatumType = {
      value,
    };
    this.addNode(customNode, hydratedCustomNode);
    return customNode;
  }
}
